import { computed, ref, watch, watchEffect } from 'vue';

import type { CartsTransactionsApi } from '@/services/booking/cartsTransactionsApi';
import type {
  BookingContactField,
  CartDetailsItemResponse,
  SaveParticipantsRequest,
} from '@/types/carts-transactions-api';

export type FormField = {
  field: BookingContactField;
  formKey: string;
};

export type RenderableGroup = {
  groupKey: string;
  title: string;
  formFields: FormField[];
};

type UnitContactGroup = {
  unitId: string;
  unitName: string;
  participantIndex: number;
  fields: BookingContactField[];
};

// Based on the HTML5 spec pattern for input type="email", but stricter: the domain must contain
// a dot and end with an alphabetic TLD of 2+ letters, so "jean@gmailcom" or "jean@localhost" are
// rejected (providers like Yoplanning refuse them and the booking fails).
// https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input/email#basic_validation
export function isValidEmail(value: string): boolean {
  return /^[\w.!#$%&'*+/=?^`{|}~-]+@(?:[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?\.)+[a-z]{2,63}$/i.test(value);
}

// Strips common formatting chars (spaces, dashes, parens, dots, leading +) then
// checks the remaining digits fall within the E.164 range of 7–15.
export function isValidPhone(value: string): boolean {
  const digits = value.replace(/[\s\-()+.]/g, '');
  return /^\d{7,15}$/.test(digits);
}

// Native date inputs emit ISO local dates (yyyy-MM-dd)
export function isValidDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function getGroupKey(group: UnitContactGroup): string {
  return `${group.unitId}-${group.participantIndex}`;
}

function getFieldSourceId(field: BookingContactField): string {
  return field.sourceId || field.name;
}

export function useContactFieldsForm(
  item: () => CartDetailsItemResponse,
  cartsTransactionsApi: () => CartsTransactionsApi,
  onCompleted: (value: boolean) => void,
) {
  const isCompleted = ref(false);
  const isSaving = ref(false);
  const saveError = ref<string | null>(null);
  const formData = ref<Record<string, string>>({});
  const savedFormData = ref<Record<string, string>>({});

  const unitContactGroups = computed<UnitContactGroup[]>(() => {
    const bookingDetails = item().bookingDetails;
    if (!bookingDetails)
      return [];

    const bookingUnitsMap = new Map(bookingDetails.units.map(unit => [unit.unitId, unit]));
    const groups: UnitContactGroup[] = [];

    for (const cartUnit of item().units) {
      const bookingUnit = bookingUnitsMap.get(cartUnit.sourceUnitId);
      if (!bookingUnit || bookingUnit.requiredContactFields.length === 0)
        continue;

      for (let participantNumber = 1; participantNumber <= cartUnit.quantity; participantNumber++) {
        groups.push({
          unitId: bookingUnit.unitId,
          unitName: bookingUnit.name,
          participantIndex: participantNumber,
          fields: bookingUnit.requiredContactFields,
        });
      }
    }

    return groups;
  });

  const renderableGroups = computed<RenderableGroup[]>(() => {
    return unitContactGroups.value.map(group => ({
      groupKey: getGroupKey(group),
      title: `${group.unitName} ${group.participantIndex}`,
      formFields: group.fields.map(field => ({
        field,
        formKey: `${group.unitId}-${group.participantIndex}-${field.name}`,
      })),
    }));
  });

  const summaryText = computed(() => {
    const bookingDetails = item().bookingDetails;
    if (!bookingDetails)
      return '';

    const bookingUnitsMap = new Map(bookingDetails.units.map(unit => [unit.unitId, unit]));
    const parts: string[] = [];
    for (const cartUnit of item().units) {
      if (cartUnit.quantity > 0) {
        const bookingUnit = bookingUnitsMap.get(cartUnit.sourceUnitId);
        const label = bookingUnit?.name ?? cartUnit.description ?? cartUnit.sourceUnitId;
        parts.push(`${cartUnit.quantity} ${label.toLowerCase()}`);
      }
    }
    return parts.join(', ');
  });

  function getFieldValue(formKey: string, field: BookingContactField): string {
    return formData.value[formKey] ?? field.defaultValue ?? '';
  }

  function setFieldValue(formKey: string, value: string) {
    formData.value[formKey] = value;
  }

  function validateFieldsComplete(data: Record<string, string>, fields: BookingContactField[], keyPrefix: string): boolean {
    for (const field of fields) {
      if (!field.required)
        continue;
      const value = data[`${keyPrefix}-${field.name}`]?.trim();
      if (!value)
        return false;
      if (field.type === 'EMAIL' && !isValidEmail(value))
        return false;
      if (field.type === 'PHONE' && !isValidPhone(value))
        return false;
      if (field.type === 'DATE' && !isValidDate(value))
        return false;
    }
    return true;
  }

  function isFormDataComplete(data: Record<string, string>): boolean {
    if (unitContactGroups.value.length === 0)
      return false;

    for (const group of unitContactGroups.value) {
      if (!validateFieldsComplete(data, group.fields, `${group.unitId}-${group.participantIndex}`))
        return false;
    }

    return true;
  }

  function buildPersistedFormData(): Record<string, string> {
    const data: Record<string, string> = {};
    const cartUnitsMap = new Map(item().units.map(unit => [unit.sourceUnitId, unit]));

    for (const group of unitContactGroups.value) {
      const cartUnit = cartUnitsMap.get(group.unitId);
      if (!cartUnit)
        continue;
      const participant = cartUnit.participants[group.participantIndex - 1];
      if (!participant)
        continue;

      for (const field of group.fields) {
        const persisted = participant.fieldValues.find(fieldValue => fieldValue.fieldName === getFieldSourceId(field));
        if (persisted?.value) {
          data[`${group.unitId}-${group.participantIndex}-${field.name}`] = persisted.value;
        }
      }
    }

    return data;
  }

  watchEffect(() => {
    if (!item().bookingDetails)
      return;
    if (Object.keys(savedFormData.value).length > 0)
      return;

    const persisted = buildPersistedFormData();
    if (Object.keys(persisted).length > 0) {
      savedFormData.value = persisted;
      formData.value = { ...persisted };
      if (isFormDataComplete(persisted)) {
        isCompleted.value = true;
      }
    }
  });

  const isFormValid = computed(() => isFormDataComplete(formData.value));

  watch(isCompleted, value => onCompleted(value), { immediate: true });

  function resetFormToSaved() {
    formData.value = { ...savedFormData.value };
  }

  async function saveForm(errorMessage: string) {
    if (!isFormValid.value)
      return;

    isSaving.value = true;
    saveError.value = null;

    const payload: SaveParticipantsRequest = {
      participants: unitContactGroups.value.map((group) => {
        const cartUnit = item().units.find(unit => unit.sourceUnitId === group.unitId);
        return {
          unitId: cartUnit!.unitId,
          participantIndex: group.participantIndex,
          fields: group.fields
            .map(field => ({
              name: getFieldSourceId(field),
              value: (formData.value[`${group.unitId}-${group.participantIndex}-${field.name}`] ?? '').trim(),
            }))
            .filter(field => field.value !== ''),
        };
      }),
    };

    try {
      await cartsTransactionsApi().saveParticipants(item().itemId, payload);
      savedFormData.value = { ...formData.value };
      isCompleted.value = true;
    }
    catch (error) {
      console.error('Error saving participants', error);
      saveError.value = errorMessage;
    }
    finally {
      isSaving.value = false;
    }
  }

  return {
    isCompleted,
    isSaving,
    saveError,
    renderableGroups,
    summaryText,
    isFormValid,
    getFieldValue,
    setFieldValue,
    resetFormToSaved,
    saveForm,
  };
}
